import { Mail, MessageCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useSiteBranding } from "@/context/SiteContext";
import { useSeo } from "@/hooks/useSeo";
import { api } from "@/lib/api";

const defaultForm = { name: "", phone: "", email: "", notes: "", inquiry_type: "contact" };

export const ContactPage = () => {
  const [form, setForm] = useState(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const { branding, getWhatsAppHref, isAgentPath, requestParams } = useSiteBranding();

  useSeo({
    title: "Contact SG Prop Portal | SG Prop Portal",
    description: "WhatsApp, email, or send a direct enquiry to Team SG Prop Portal through the portal.",
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      await api.post(
        "/contact-lead",
        {
          ...form,
          lead_source: isAgentPath ? "agent_portal" : "homepage",
          capture_context: isAgentPath ? `${branding.name} contact page` : "Homepage contact form",
          captured_route: window.location.pathname,
        },
        { params: requestParams },
      );
      toast.success("Your enquiry has been sent successfully.");
      setForm(defaultForm);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to submit at the moment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="section-shell">
        <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr]">
          <div className="luxury-panel p-6 md:p-8">
            <p className="subtle-label">Direct Contact</p>
            <h1 className="mt-3 text-4xl font-semibold text-white md:text-5xl" data-testid="contact-page-heading">
              {isAgentPath ? `Directly contact ${branding.name}.` : "One portal, one response team, one clean follow-up path."}
            </h1>
            <div className="mt-6 space-y-4 text-sm leading-7 text-slate-300">
              <p data-testid="contact-owner-name">{branding.name}</p>
              <p data-testid="contact-owner-email">{branding.email}</p>
              <p data-testid="contact-owner-mobile">{branding.whatsapp_display}</p>
              {branding.cea_registration_number ? <p data-testid="contact-owner-cea">CEA {branding.cea_registration_number}</p> : null}
            </div>
            <div className="mt-8 grid gap-3">
              <a className="gold-button !w-full" data-testid="contact-whatsapp-button" href={getWhatsAppHref()} rel="noreferrer" target="_blank">
                <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp the Team
              </a>
              <a className="outline-button !w-full" data-testid="contact-email-button" href={`mailto:${branding.email}`}>
                <Mail className="mr-2 h-4 w-4" /> Email the Team
              </a>
            </div>
          </div>

          <form className="luxury-panel grid gap-4 p-6 md:p-8" onSubmit={handleSubmit}>
            <h2 className="text-3xl font-semibold text-white">Send a direct property enquiry</h2>
            <input className="field-shell" data-testid="contact-name-input" onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Full Name" required value={form.name} />
            <input className="field-shell" data-testid="contact-phone-input" onChange={(event) => setForm({ ...form, phone: event.target.value })} placeholder="Mobile Number" required value={form.phone} />
            <input className="field-shell" data-testid="contact-email-input" onChange={(event) => setForm({ ...form, email: event.target.value })} placeholder="Email Address" required type="email" value={form.email} />
            <textarea className="field-shell min-h-40" data-testid="contact-message-input" onChange={(event) => setForm({ ...form, notes: event.target.value })} placeholder="Tell us what you're looking for" value={form.notes} />
            <button className="gold-button w-full" data-testid="contact-submit-button" disabled={submitting} type="submit">
              Send Enquiry
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};